<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'PaymentsView',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    parameters: {
      type: Object,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const getItems = (...args) => {
    if (props.parameters) {
      return $fn.value.fetch('list_payment', { parameters: props.parameters });
    }

    const parameters = params.value;
    return parameters && parameters.account && parameters.date_from
      ? $fn.value.fetch('list_payment', { parameters })
      : [];
  };
  const getInvoiceBadgeClass = (...args) => {
    const data = args[0];

    if (_.isEmpty(data)) {
      return;
    }

    const payment_balance = data.payment_balance || 0;
    const invoice_gross_amount = data.invoice_gross_amount;

    if (payment_balance == 0) {
      return 'bg-green-200';
    }

    if (payment_balance < 0) {
      return 'bg-yellow-200';
    }

    if (payment_balance < invoice_gross_amount) {
      return 'bg-blue-200';
    }
  };

  const selectedItems = ref([]);
  const params = ref({});
  const totals = ref([
    {
      id: 0,
      amount_in: 0,
      amount_out: 0,
    },
  ]);
  const paymentMethods = ref(null);
  const ux = ref([]);
  const b_2248226175642056_headerShowColumnSelectorButton = computed(
    () => false
  );
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: false,
    filterable: true,
    resizable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'payment_method',
      name: $fn.value.pgettext('PaymentBrowserView', 'Method'),
      formatter: {
        name: 'gettext',
        context: 'PaymentMethod',
        conversion: ['Unsnake', 'Sentence'],
        filterInput: true,
      },
    },
    {
      field: 'transaction_number',
      name: $fn.value.pgettext('PaymentBrowserView', 'Number'),
      type: 'command',
      formatter: [
        {
          name: 'chain',
          formatters: [
            {
              name: 'link',
              href: '#',
            },
            {
              name: 'cellclass',
              value: (row) => ({
                'fw-bold': true,
                'bg-yellow-200': !row.locked,
              }),
            },
          ],
        },
      ],
    },
    {
      field: 'transaction_type',
      name: $fn.value.pgettext('PaymentBrowserView', 'Type'),
      formatter: {
        name: 'gettext',
        context: 'TransactionType',
        conversion: 'Unsnake',
        filterInput: true,
      },
    },
    {
      field: 'transaction_date',
      name: $fn.value.pgettext('PaymentBrowserView', 'Date'),
      type: 'date',
    },
    {
      field: 'transaction_title',
      name: $fn.value.pgettext('PaymentBrowserView', 'Title'),
    },
    {
      field: 'description',
      name: $fn.value.pgettext('PaymentBrowserView', 'Description'),
    },
    {
      field: 'notes',
      name: $fn.value.pgettext('PaymentBrowserView', 'Notes'),
    },
    {
      field: 'opening_balance',
      name: $fn.value.pgettext('PaymentBrowserView', 'Opening balance'),
      type: 'number',
      formatter: 'number',
      noFooterTotals: true,
    },
    {
      field: 'amount_in',
      name: $fn.value.pgettext('PaymentBrowserView', 'In'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'amount_out',
      name: $fn.value.pgettext('PaymentBrowserView', 'Out'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'closing_balance',
      name: $fn.value.pgettext('PaymentBrowserView', 'Closing balance'),
      type: 'number',
      formatter: 'number',
      noFooterTotals: true,
    },
    {
      field: 'currency',
      name: $fn.value.pgettext('PaymentBrowserView', 'Currency'),
    },
    {
      field: 'exchange_rate',
      name: $fn.value.pgettext('PaymentBrowserView', 'Exchange Rate'),
      type: 'number',
      formatter: 'number',
      noFooterTotals: true,
    },
    {
      field: 'reference_number',
      name: $fn.value.pgettext('PaymentBrowserView', 'Ref Nr.'),
    },
    {
      field: 'file_number',
      name: $fn.value.pgettext('PaymentBrowserView', 'File Nr.'),
    },
    {
      field: 'company_name',
      name: $fn.value.pgettext('PaymentBrowserView', 'Own company'),
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'company_id',
        },
      },
    },
    {
      field: 'client_name',
      name: $fn.value.pgettext('PaymentBrowserView', 'Client'),
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'client_id',
        },
      },
    },
    {
      field: 'invoices',
      name: $fn.value.pgettext('PaymentBrowserView', 'Invoices'),
      formatter: [
        {
          name: 'chain',
          mode: 'mapReduce',
          separator: ', ',
          formatterContext: 'value',
          formatters: [
            {
              name: 'getvalue',
              opts: {
                field: 'invoice_number',
                source: 'value',
              },
            },
            {
              name: 'routeLink',
              to: 'invoice',
              navigateOnIconClick: true,
              params: {
                id: 'id',
              },
            },
            {
              name: 'badge',
              badgeClass: getInvoiceBadgeClass,
            },
          ],
        },
      ],
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    params: params.value,
    totals: totals.value,
    paymentMethods: paymentMethods.value,
    ux: ux.value,
    b_2248226175642056_headerShowColumnSelectorButton:
      b_2248226175642056_headerShowColumnSelectorButton.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_paymentMethods = async () => {
    return $fn.value
      .fetch('list_payment_method')
      .then((data) =>
        _.reduce(data, (m, v, k) =>
          _.set(m, v.id, $fn.value.pgettext('PaymentMethod', v.name))
        )
      );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      { name: 'params' },
      { name: 'totals' },
      { name: 'paymentMethods' },
      { name: 'ux' },
      {
        name: 'b_2248226175642056_headerShowColumnSelectorButton',
        nodeId: '2248226175642056',
      },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { paymentMethods: $$load_paymentMethods },
    ux: {},
    dataRefs: {
      selectedItems,
      params,
      totals,
      paymentMethods,
      ux,
      b_2248226175642056_headerShowColumnSelectorButton,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.parameters,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parameters');
    }
  );

  const act_2248226175642056_setData_hmx3irqld_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_showModal_pzou023gi_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        modal: true,
        oid: $event.data.data.id,
      },
    };
  };
  const inst_evh_2248226175642056_command_njgm5rjv4_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'transaction_number';
  };

  const act_7315092382398562_reloadComponentData_kt3pshc02 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_kwyxwny53 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_kt3pshc02,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_kwyxwny53],
    event: 'reload',
  });
  const act_2248226175642056_setData_hmx3irqld = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_hmx3irqld_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_uxk1oudcu =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_hmx3irqld,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_uxk1oudcu],
      event: 'selectedRowsChanged',
    });
  const act_2248226175642056_showModal_pzou023gi =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_pzou023gi_getActionArgs,
    });
  const inst_evh_2248226175642056_command_njgm5rjv4 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_pzou023gi,
      event: 'command',
      when: inst_evh_2248226175642056_command_njgm5rjv4_getWhen,
      displayName: 'showModal',
    });
  const inst_evh_2248226175642056_command = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_command_njgm5rjv4],
    event: 'command',
  });

  const actions = {
    inst_evh_7315092382398562_reload_kwyxwny53,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_uxk1oudcu,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2248226175642056_command_njgm5rjv4,
    inst_evh_2248226175642056_command,
  };

  const evh_7315092382398562_reload_kwyxwny53 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_kwyxwny53.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_uxk1oudcu = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_uxk1oudcu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_njgm5rjv4 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_njgm5rjv4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <VueSlickgrid
      name="payments"
      :headerTitle="pgettext('PaymentsView', 'Payments')"
      :items="getItems"
      checkbox-row-selection
      :headerShowColumnSelectorButton="
        b_2248226175642056_headerShowColumnSelectorButton
      "
      header-show-reload-button
      footer-totals
      export-to-csv
      :exportToCsvFilename="pgettext('ExportFilename', 'payments')"
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
      @command="evh_2248226175642056_command($event, {})"
    >
      <slot> </slot>
    </VueSlickgrid>
  </ItpUsercontrol>
</template>
